// Types for Bridge Bidding Problems Practice feature
// Source: specs/001-bidding-problems-practice/data-model.md
// Extended: specs/003-difficulty-level-selector/data-model.md

// T071: Session origin tracking for UI behavior control
// v1.4: Added 'important-sequences' and 'review-problems' origins
export type SessionOrigin =
  | 'dashboard'              // From Dashboard page (existing)
  | 'component-selector'     // DEPRECATED - keep for backward compatibility
  | 'difficulty-selector'    // From "Level of Difficulty" dropdown option
  | 'important-sequences'    // From "Important Sequences" dropdown option (v1.4)
  | 'review-problems'        // From "Review Problems" dropdown option (v1.4)
  | 'practice-opening-bid'                    // From Practice > Opening bid
  | 'practice-responding-no-competition'      // From Practice > Responding without competition
  | 'practice-responding-with-competition'     // From Practice > Responding with competition
  | 'practice-responder-rebid-no-competition'  // From Practice > Responder's rebid without competition
  | 'practice-responder-rebid-with-competition' // From Practice > Responder's rebid with competition
  | 'overcalling'              // From Practice > Competitive Bidding > Overcalling
  | '3rd-level-bid';            // From Practice > Competitive Bidding > Decision to make a 3rd level bid (feature 013)

// Feature 026: the two re-drillable modes that store a per-user resume position server-side.
// The values match the session-position API's {feature} path parameter and the DB CHECK.
export type SessionFeatureKey = 'important-sequences' | 'review-problems';

// T071: Configuration for each session origin (v1.4, v1.8)
// v1.8: Added showsScoring, showsEndSession, hasNavGuard, navigatesToSummary, recordsHistory
export interface SessionOriginConfig {
  showSlider: boolean;          // Whether mid-session slider is shown
  allowAddToReview: boolean;    // Whether "Add to Review" button is shown
  showRemoveFromReview: boolean; // Whether "Remove from Review" is shown
  showsScoring: boolean;        // v1.8: Whether scoring is displayed
  showsEndSession: boolean;     // v1.8: Whether "End Session" button is shown
  hasNavGuard: boolean;         // v1.8: Whether navigation confirmation is shown
  navigatesToSummary: boolean;  // v1.8: Whether session ends with Summary page
  recordsHistory: boolean;      // v1.8: Whether answers are recorded in user_problem_history
}

export const SESSION_ORIGIN_CONFIG: Record<SessionOrigin, SessionOriginConfig> = {
  'dashboard': {
    showSlider: false,
    allowAddToReview: true,
    showRemoveFromReview: false,
    showsScoring: true,
    showsEndSession: true,
    hasNavGuard: true,
    navigatesToSummary: true,
    recordsHistory: true
  },
  'component-selector': {
    showSlider: false,
    allowAddToReview: true,
    showRemoveFromReview: false,
    showsScoring: true,
    showsEndSession: true,
    hasNavGuard: true,
    navigatesToSummary: true,
    recordsHistory: true
  },
  'difficulty-selector': {
    showSlider: true,
    allowAddToReview: true,
    showRemoveFromReview: false,
    showsScoring: true,
    showsEndSession: true,
    hasNavGuard: true,
    navigatesToSummary: true,
    recordsHistory: true
  },
  'important-sequences': {
    showSlider: false,
    allowAddToReview: true,
    showRemoveFromReview: false,
    showsScoring: true,
    showsEndSession: true,
    hasNavGuard: true,
    navigatesToSummary: true,
    recordsHistory: false  // v1.8 (FR-102): NOT recorded in history
  },
  'review-problems': {
    showSlider: false,
    allowAddToReview: false,
    showRemoveFromReview: true,
    showsScoring: true,         // v1.8 (FR-104, FR-105): Scoring enabled
    showsEndSession: true,      // v1.8 (FR-106): End Session button shown
    hasNavGuard: true,          // v1.8 (FR-106): Navigation confirmation applies
    navigatesToSummary: true,   // v1.8 (FR-107): Navigates to Summary page
    recordsHistory: false       // v1.8 (FR-108): NOT recorded in history
  },
  'practice-opening-bid': {
    showSlider: false,
    allowAddToReview: false,
    showRemoveFromReview: false,
    showsScoring: true,
    showsEndSession: true,
    hasNavGuard: true,
    navigatesToSummary: true,
    recordsHistory: false
  },
  'practice-responding-no-competition': {
    showSlider: false,
    allowAddToReview: false,
    showRemoveFromReview: false,
    showsScoring: true,
    showsEndSession: true,
    hasNavGuard: true,
    navigatesToSummary: true,
    recordsHistory: false
  },
  'practice-responding-with-competition': {
    showSlider: false,
    allowAddToReview: false,
    showRemoveFromReview: false,
    showsScoring: true,
    showsEndSession: true,
    hasNavGuard: true,
    navigatesToSummary: true,
    recordsHistory: false
  },
  'practice-responder-rebid-no-competition': {
    showSlider: false,
    allowAddToReview: false,
    showRemoveFromReview: false,
    showsScoring: true,
    showsEndSession: true,
    hasNavGuard: true,
    navigatesToSummary: true,
    recordsHistory: false
  },
  'practice-responder-rebid-with-competition': {
    showSlider: false,
    allowAddToReview: false,
    showRemoveFromReview: false,
    showsScoring: true,
    showsEndSession: true,
    hasNavGuard: true,
    navigatesToSummary: true,
    recordsHistory: false
  },
  'overcalling': {
    showSlider: false,
    allowAddToReview: true,
    showRemoveFromReview: false,
    showsScoring: true,
    showsEndSession: true,
    hasNavGuard: true,
    navigatesToSummary: true,
    recordsHistory: false
  },
  // Feature 013 — isolated tournament-hand mode. No review (its ids are tableresult ids, not
  // problem ids) and no history recording; otherwise it behaves like a scored practice loop.
  '3rd-level-bid': {
    showSlider: false,
    allowAddToReview: false,
    showRemoveFromReview: false,
    showsScoring: true,
    showsEndSession: true,
    hasNavGuard: true,
    navigatesToSummary: true,
    recordsHistory: false
  },
};

// T001: Difficulty category and range types for difficulty level selector feature
export type DifficultyCategory = 'beginner' | 'intermediate' | 'advanced' | 'expert';

export interface DifficultyConfig {
  name: string;        // Display name
  min: number;         // Minimum difficulty level
  max: number;         // Maximum difficulty level
  hasSlider: boolean;  // Whether range slider is shown
}

export interface DifficultyRange {
  minLevel: number;  // Selected minimum (1-10)
  maxLevel: number;  // Selected maximum (1-10)
}

// T002: Difficulty categories constant configuration
export const DIFFICULTY_CATEGORIES: Record<DifficultyCategory, DifficultyConfig> = {
  beginner: { name: 'Beginner', min: 1, max: 2, hasSlider: false },
  intermediate: { name: 'Intermediate', min: 1, max: 4, hasSlider: true },
  advanced: { name: 'Advanced', min: 2, max: 5, hasSlider: true },
  expert: { name: 'Expert', min: 4, max: 10, hasSlider: true },
} as const;

export interface AuctionBid {
  seat: 'N' | 'E' | 'S' | 'W';
  call: string;
  comment?: string | null;
  /** Per-bid BBO alert text (cardsgamehands.BiddingSequence.Alert). Null/absent when no alert. */
  alert?: string | null;
}

export interface Hand {
  spades: string;
  hearts: string;
  diamonds: string;
  clubs: string;
}

export interface Problem {
  id: number;
  serial?: string | null;        // v2.4 (FR-157): Serial number from DB column
  answer?: string | null;        // v2.4 (FR-165): Answer from DB column
  source: string | null;
  scoring: string;
  vulnerability: string;
  vulnerabilityText: string; // Raw vulnerability text from JSON (e.g., "Favorable", "Unfavorable")
  difficultyLevel: number | null; // v2 (feature 008 scope extension 2026-05-20): nullable so tournament-hand items can omit difficulty (per spec A1 — no synthetic default).
  auction: AuctionBid[];
  playerHand: Hand;
  decisionSeat: 'N' | 'E' | 'S' | 'W';
  question: string | null;
  // Feature 013 editor mode only (Session 2026-06-15, FR-028). Present on items returned to an
  // Admin/Editor; absent (undefined) for public sessions and all other modes.
  isAnswered?: boolean;  // per-editor "Answered" status
  isAssigned?: boolean;  // global "Assigned" (difficulty) status
  // Editor read-only review of an already-answered hand (Session 2026-06-15): the editor's own
  // recorded call and Jeff's actual call. Both present only when isAnswered is true (the server
  // withholds jeffBid for unanswered hands to preserve answer secrecy).
  editorAnswer?: string | null;
  jeffBid?: string | null;
  editorAnswerCorrect?: boolean; // server-computed verdict for the answered-hand review
  // Jeff's expert comment (FR-034, Session 2026-07-01), present only for an already-answered hand
  // that carries one — same answered-only gate as jeffBid. Drives the "Jeff says:" section in the
  // editor read-only review of an answered hand. Null/undefined for unanswered/public.
  jeffComment?: string | null;
}

export interface AnswerResult {
  isCorrect: boolean;
  correctBid: string;
  expertName: 'Jeff' | 'Ish';
  expertComment: string;
  /**
   * Feature 027 — the AI engine's reasoning for the answered call, present ONLY where the engine
   * predicted the call Jeff actually made and the hand carries no comment of Jeff's own. Raw
   * reasoning: the page adds the "AI predicted as Jeff because" prefix and shows "Information not
   * available" in its place when this is absent.
   */
  aiComment?: string | null;
  /**
   * Feature 027 — true when this item came from the downloaded tournament pool rather than the
   * local problems table. The Overcalling question DTO deliberately hides an item's source
   * (feature 008, FR-017c), so this is how the answer response tells the page whether the AI
   * panel applies at all. Absent/false ⇒ a local problem ⇒ no AI panel, ever.
   */
  isTournamentHand?: boolean;
}

export interface SessionSummary {
  totalQuestions: number;
  answered: number;
  skipped: number;
  correct: number;
  incorrect: number;
  correctByDifficulty: Record<number, number>;
  /**
   * The longest winning run of the visit (feature 023). Optional because a summary handed over by
   * an older session — one persisted before the streak existed — carries no run at all, and 0 would
   * claim the learner never got two in a row.
   */
  bestStreak?: number;
}

export type VulnerabilityMap = {
  N: boolean;
  E: boolean;
  S: boolean;
  W: boolean;
};

// T003: StartSessionRequest — a difficulty range is the only selection the endpoint accepts
// (the legacy componentId / openingBids fields were removed in the 2026-10-06 cleanup)
export interface StartSessionRequest {
  minDifficulty: number;     // minimum difficulty level (1-10)
  maxDifficulty: number;     // maximum difficulty level (1-10)
}

export interface SessionResponse {
  problems: Problem[];
  totalAvailable: number;
}

export interface SubmitAnswerRequest {
  bid: string;
  sessionOrigin?: SessionOrigin;  // v1.8: Optional session origin for conditional history recording
  // The feature 008 v2 `kind` discriminator ('problem' | 'hand') was dropped from the client in
  // feature 033: the overcalling page serves teaching problems only. The server still accepts
  // the field on the wire and ignores it.
}

export interface SubmitAnswerResponse {
  isCorrect: boolean;
  correctBid: string;
  expertName: 'Jeff' | 'Ish';
  expertComment: string;
  /**
   * Feature 027 — the AI engine's reasoning for the answered call, present ONLY where the engine
   * predicted the call Jeff actually made and the hand carries no comment of Jeff's own. Raw
   * reasoning: the page adds the "AI predicted as Jeff because" prefix and shows "Information not
   * available" in its place when this is absent.
   */
  aiComment?: string | null;
  /**
   * Feature 027 — true when this item came from the downloaded tournament pool rather than the
   * local problems table. The Overcalling question DTO deliberately hides an item's source
   * (feature 008, FR-017c), so this is how the answer response tells the page whether the AI
   * panel applies at all. Absent/false ⇒ a local problem ⇒ no AI panel, ever.
   */
  isTournamentHand?: boolean;
}

export interface ErrorResponse {
  error: string;
  message: string;
}

export interface RateLimitErrorResponse {
  error: string;
  message: string;
  retryAfter: number;
}

// T072: Review problem interface (v1.4)
export interface ReviewProblem {
  problemId: number;
  addedAt: string;              // ISO timestamp
  problem: Problem;             // Full problem data
}

// T072: Review problems response (v1.4)
export interface ReviewProblemsResponse {
  problems: ReviewProblem[];
  totalCount: number;
}

// T072: Review action response (v1.4)
export interface ReviewActionResponse {
  success: boolean;
  message?: string;
}

// T072: Clear history response (v1.4)
export interface ClearHistoryResponse {
  success: boolean;
  deletedCount: number;
  message?: string;
}

// =============================================================================
// Legal Bid Validation Types (004-legal-bid-validation)
// Source: specs/004-legal-bid-validation/data-model.md
// =============================================================================

/**
 * Type of call in an auction: regular bid, pass, double, redouble, or placeholder
 */
export type BidType = 'bid' | 'pass' | 'double' | 'redouble' | 'placeholder' | 'unknown';

/**
 * Represents a normalized bid parsed from auction data.
 * When type is 'bid', level and suit are present.
 * When type is 'pass', 'double', 'redouble', 'placeholder', or 'unknown', level and
 * suit are undefined. 'unknown' is returned for malformed/unrecognised call tokens
 * (e.g. stray LIN control tags that leak into auction data) so a single bad call
 * degrades gracefully instead of throwing and blank-screening the auction display.
 */
export interface ParsedBid {
  type: BidType;
  level?: number;      // 1-7, only present when type === 'bid'
  suit?: string;       // 'C' | 'D' | 'H' | 'S' | 'NT', only present when type === 'bid'
}

/**
 * Represents the current highest suit/NT bid in the auction.
 * Excludes Pass, Double, and Redouble calls.
 */
export interface HighestBid {
  level: number;       // 1-7
  suit: string;        // 'C' | 'D' | 'H' | 'S' | 'NT'
  value: number;       // Pre-computed: (level * 5) + SUIT_ORDER[suit]
}

/**
 * Current double/redouble status of the auction.
 * - 'none': No suit/NT bid has been made yet
 * - 'undoubled': A suit/NT bid has been made, not doubled
 * - 'doubled': The current contract has been doubled
 * - 'redoubled': The current contract has been redoubled
 */
export type ContractState = 'none' | 'undoubled' | 'doubled' | 'redoubled';

/**
 * Represents a partnership in bridge (North-South or East-West).
 */
export type Side = 'NS' | 'EW';

/**
 * Represents a player position at the table.
 */
export type Position = 'N' | 'E' | 'S' | 'W';

/**
 * Complete state of the auction needed for bid validation.
 */
export interface AuctionState {
  /** The highest suit/NT bid made so far, or null if only passes */
  highestBid: HighestBid | null;

  /** Current double/redouble status */
  contractState: ContractState;

  /** Which side made the last suit/NT bid (for double/redouble validation) */
  lastBidSide: Side | null;

  /** Whose turn it is to bid */
  currentBidder: Position;

  /** Total number of calls made (for position calculation) */
  callCount: number;
}

/**
 * Output from the useLegalBids hook, consumed by BidInput component.
 */
export interface LegalBids {
  /** Levels that have at least one legal suit */
  levels: number[];

  /** Legal suits for the currently selected level */
  suits: string[];

  /** Whether Pass is available (always true per bridge rules) */
  canPass: boolean;

  /** Whether Double is available */
  canDouble: boolean;

  /** Whether Redouble is available */
  canRedouble: boolean;
}

// T054: Persisted session state for localStorage (FR-031, FR-032)
export interface PersistedSessionState {
  problems: Problem[];
  allProblems: Problem[];
  currentIndex: number;
  score: { correct: number; incorrect: number; skipped: number };
  results: Array<{
    problemId: number;
    difficultyLevel: number | null; // null for a 3rd-level-bid tournament hand (the overcalling page is problems-only since feature 033)
    isCorrect: boolean | null;
    correctBid?: string;
    expertComment?: string;
  }>;
  sessionOrigin: SessionOrigin | null;
  originalCategory: DifficultyCategory | null;
  categoryBounds: { min: number; max: number } | null;
  currentRange: DifficultyRange | null;
  answeredProblemIds: number[];
  // The current run and the visit's best (feature 023). Optional: snapshots written before the
  // streak existed restore as 0 rather than undefined, which would render "Streak: undefined".
  streak?: number;
  bestStreak?: number;
  // What "Previous" replays, keyed by problem id (client request, 2026-08-03). Optional for the
  // same reason as the streak fields: snapshots written before it existed restore as {}, which
  // costs a refreshed session its replayable verdicts but never breaks the restore.
  answerRecords?: Record<number, {
    result: AnswerResult | null;
    userBid: string | null;
    skipped: boolean;
  }>;
  // Opener-based sub-mode key (`<subMode>|<openingBid>`) for the "3rd-level-bid" mode only
  // (Session 2026-06-08). Restored only when it matches the current URL slice, so switching
  // sub-accordions starts a fresh session. Undefined for all other modes.
  thirdLevelSubModeKey?: string;
  savedAt: number; // Timestamp for expiry check
}
